import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { alpha, useTheme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'

import { adminApi } from '../../api/endpoints'
import { useLocale } from '../../i18n/LocaleProvider'
import { useFormatters } from '../../hooks/useFormatters'
import { ErrorState } from '../../components/common/States'
import type { AdminDayAvailability, AvailabilityStatus } from '../../types/api'

function toIso(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`
}

function addMonths(date: Date, count: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + count, 1)
}

/**
 * The owner's calendar.
 *
 * Three months at a time, one unit at a time, with drag-free range selection: click the first
 * night, click the last, then act. A five-cottage operator edits a range far more often than
 * a single night (a fortnight closed for renovation, July at the festival rate), so the range
 * is the primary gesture and a single night is just a range of one.
 *
 * Nights held by a booking are shown with the guest's name and cannot be edited here. Freeing
 * them means cancelling the booking, which is a decision with a person on the other end of it
 * and belongs on the bookings screen, not behind a calendar drag.
 */
export function AdminCalendarPage() {
  const { t } = useTranslation()
  const { locale } = useLocale()
  const theme = useTheme()
  const { money, monthYear, weekdayNarrow } = useFormatters()
  const queryClient = useQueryClient()

  const [unitId, setUnitId] = useState<string>('')
  const [cursor, setCursor] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1))
  const [selection, setSelection] = useState<{ from: string | null; to: string | null }>({
    from: null,
    to: null,
  })
  const [price, setPrice] = useState('')
  const [minNights, setMinNights] = useState('')
  const [note, setNote] = useState('')

  const units = useQuery({ queryKey: ['admin', 'units', locale], queryFn: () => adminApi.units(locale) })

  const activeUnitId = unitId || units.data?.[0]?.id || ''

  const { from, to } = useMemo(
    () => ({ from: toIso(cursor), to: toIso(addMonths(cursor, 3)) }),
    [cursor],
  )

  const calendar = useQuery({
    queryKey: ['admin', 'calendar', activeUnitId, from, to],
    queryFn: () => adminApi.calendar(activeUnitId, from, to),
    enabled: Boolean(activeUnitId),
    staleTime: 0,
  })

  const update = useMutation({
    mutationFn: (payload: {
      status?: AvailabilityStatus
      price?: number | null
      minNights?: number | null
      note?: string | null
      clearPrice: boolean
    }) =>
      adminApi.updateCalendar(activeUnitId, {
        from: selection.from!,
        // The API's range is exclusive, and the owner selected an inclusive last night.
        to: toIso(new Date(new Date(`${selection.to ?? selection.from}T00:00:00`).getTime() + 86_400_000)),
        ...payload,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'calendar'] })
      setSelection({ from: null, to: null })
      setPrice('')
      setMinNights('')
      setNote('')
    },
  })

  const byDate = useMemo(() => {
    const map = new Map<string, AdminDayAvailability>()
    for (const day of calendar.data ?? []) map.set(day.date, day)
    return map
  }, [calendar.data])

  const weekdayLabels = useMemo(() => {
    const monday = new Date(2024, 0, 1)
    return Array.from({ length: 7 }, (_, index) =>
      weekdayNarrow(new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + index)),
    )
  }, [weekdayNarrow])

  const handleDayClick = (iso: string) => {
    if (!selection.from || selection.to) {
      setSelection({ from: iso, to: null })
      return
    }
    if (iso < selection.from) {
      setSelection({ from: iso, to: selection.from })
      return
    }
    setSelection({ from: selection.from, to: iso })
  }

  const selectedCount =
    selection.from && selection.to
      ? Math.round(
          (new Date(`${selection.to}T00:00:00`).getTime() -
            new Date(`${selection.from}T00:00:00`).getTime()) /
            86_400_000,
        ) + 1
      : selection.from
        ? 1
        : 0

  return (
    <Box>
      <Typography variant="h2" sx={{ mb: 3 }}>
        {t('admin.calendar')}
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }} alignItems="center">
        <TextField
          select
          size="small"
          label={t('admin.units')}
          value={activeUnitId}
          onChange={(event) => setUnitId(event.target.value)}
          sx={{ minWidth: 240 }}
        >
          {(units.data ?? []).map((unit) => (
            <MenuItem key={unit.id} value={unit.id}>
              {unit.name}
            </MenuItem>
          ))}
        </TextField>

        <Stack direction="row" alignItems="center" spacing={1} sx={{ msAuto: 0, ml: { sm: 'auto' } }}>
          <IconButton onClick={() => setCursor(addMonths(cursor, -1))} aria-label={t('calendar.previousMonth')}>
            <ChevronLeftIcon />
          </IconButton>
          <Typography variant="subtitle1" sx={{ minWidth: 150, textAlign: 'center' }}>
            {monthYear(cursor)}
          </Typography>
          <IconButton onClick={() => setCursor(addMonths(cursor, 1))} aria-label={t('calendar.nextMonth')}>
            <ChevronRightIcon />
          </IconButton>
        </Stack>
      </Stack>

      {calendar.isError && <ErrorState error={calendar.error} onRetry={() => void calendar.refetch()} />}

      <Box
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 320px' },
          alignItems: 'start',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gap: 3,
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            opacity: calendar.isFetching ? 0.5 : 1,
          }}
        >
          {[0, 1, 2].map((offset) => {
            const month = addMonths(cursor, offset)
            const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate()
            const leadingBlanks = (new Date(month.getFullYear(), month.getMonth(), 1).getDay() + 6) % 7

            return (
              <Paper
                key={offset}
                elevation={0}
                sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 3 }}
              >
                <Typography variant="subtitle2" align="center" sx={{ mb: 1.5, fontWeight: 700 }}>
                  {monthYear(month)}
                </Typography>

                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.4 }}>
                  {weekdayLabels.map((label, index) => (
                    <Typography
                      key={index}
                      variant="caption"
                      align="center"
                      sx={{ color: 'text.secondary', fontWeight: 700 }}
                    >
                      {label}
                    </Typography>
                  ))}

                  {Array.from({ length: leadingBlanks }).map((_, index) => (
                    <Box key={`b-${index}`} />
                  ))}

                  {Array.from({ length: daysInMonth }, (_, index) => {
                    const iso = toIso(new Date(month.getFullYear(), month.getMonth(), index + 1))
                    const day = byDate.get(iso)
                    const status = day?.status ?? 'AVAILABLE'
                    const booked = status === 'BOOKED'

                    const inSelection =
                      selection.from != null &&
                      (selection.to
                        ? iso >= selection.from && iso <= selection.to
                        : iso === selection.from)

                    const colors: Record<AvailabilityStatus, string> = {
                      AVAILABLE: 'transparent',
                      BOOKED: alpha(theme.palette.success.main, 0.24),
                      BLOCKED: alpha(theme.palette.text.primary, 0.14),
                      EXTERNAL: alpha(theme.palette.warning.main, 0.24),
                    }

                    return (
                      <Tooltip
                        key={iso}
                        title={
                          day?.bookingReference
                            ? `${day.bookingReference} · ${day.guestName ?? ''}`
                            : (day?.note ?? '')
                        }
                        disableHoverListener={!day?.bookingReference && !day?.note}
                      >
                        <Box
                          component="button"
                          type="button"
                          onClick={() => handleDayClick(iso)}
                          sx={{
                            appearance: 'none',
                            border: inSelection ? `2px solid ${theme.palette.primary.main}` : '1px solid transparent',
                            font: 'inherit',
                            cursor: 'pointer',
                            p: 0.25,
                            minHeight: 42,
                            borderRadius: 1.5,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            lineHeight: 1.1,
                            bgcolor: inSelection ? alpha(theme.palette.primary.main, 0.12) : colors[status],
                            '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}` },
                          }}
                        >
                          <Box component="span" sx={{ fontSize: '0.8rem', fontWeight: booked ? 700 : 500 }}>
                            {index + 1}
                          </Box>
                          {day?.priceOverride != null && (
                            <Box component="span" sx={{ fontSize: '0.55rem', opacity: 0.75 }}>
                              {money(day.priceOverride)}
                            </Box>
                          )}
                        </Box>
                      </Tooltip>
                    )
                  })}
                </Box>
              </Paper>
            )
          })}
        </Box>

        {/* ---------------------------------------------------------------- edit panel */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            border: 1,
            borderColor: 'divider',
            borderRadius: 3,
            position: { lg: 'sticky' },
            top: { lg: 24 },
          }}
        >
          <Typography variant="h5" sx={{ mb: 1.5 }}>
            {selectedCount > 0
              ? t('common.night', { count: selectedCount })
              : t('calendar.chooseCheckIn')}
          </Typography>

          {selection.from && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {selection.from} → {selection.to ?? selection.from}
            </Typography>
          )}

          <Stack spacing={1.5}>
            <TextField
              size="small"
              type="number"
              label={t('admin.priceForNights')}
              value={price}
              onChange={(event) => setPrice(event.target.value)}
              disabled={!selection.from}
              slotProps={{ htmlInput: { min: 0, step: 5 } }}
            />
            <TextField
              size="small"
              type="number"
              label={t('admin.minNights')}
              value={minNights}
              onChange={(event) => setMinNights(event.target.value)}
              disabled={!selection.from}
              slotProps={{ htmlInput: { min: 1, max: 60 } }}
            />
            <TextField
              size="small"
              label={t('admin.ownerNote')}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              disabled={!selection.from}
            />

            <Divider sx={{ my: 0.5 }} />

            <Button
              variant="contained"
              disabled={!selection.from || update.isPending}
              onClick={() =>
                update.mutate({
                  price: price ? Number(price) : undefined,
                  minNights: minNights ? Number(minNights) : undefined,
                  note: note || undefined,
                  clearPrice: false,
                })
              }
            >
              {t('admin.applyToRange')}
            </Button>
            <Button
              variant="outlined"
              color="warning"
              disabled={!selection.from || update.isPending}
              onClick={() => update.mutate({ status: 'BLOCKED', note: note || undefined, clearPrice: false })}
            >
              🔒 {t('admin.blockDates')}
            </Button>
            <Button
              variant="outlined"
              color="success"
              disabled={!selection.from || update.isPending}
              onClick={() => update.mutate({ status: 'AVAILABLE', clearPrice: true })}
            >
              🔓 {t('admin.unblockDates')}
            </Button>

            {update.isSuccess && (
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                {t('admin.nightsUpdated', { count: update.data.nightsUpdated })}
              </Alert>
            )}
            {update.isError && <ErrorState error={update.error} />}
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Stack spacing={0.75}>
            <Legend color={alpha(theme.palette.success.main, 0.24)} label={t('booking.statusCONFIRMED')} />
            <Legend color={alpha(theme.palette.warning.main, 0.24)} label="Booking.com / Airbnb" />
            <Legend color={alpha(theme.palette.text.primary, 0.14)} label={t('calendar.unavailable')} />
          </Stack>
        </Paper>
      </Box>
    </Box>
  )
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Box sx={{ width: 14, height: 14, borderRadius: 0.75, bgcolor: color }} />
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Stack>
  )
}
